/**
 * MediaPlayback Component
 */

import React, { FC, useState, useEffect } from 'react';
import { InternalEmbed } from 'mlb-internal-embed';
import useDidUpdate from '../hooks/useDidUpdate';
import {
  MediaPlaybackProps,
  MediaPlayerOptions,
  ImplInternalEmbed,
  ImplEmbedWrapper,
} from './mediaPlayback.types';
import { getMediaConfig } from './utils/getMediaConfig';
import MediaPlaybackDiv from './mediaPlayback.style';

export const MediaPlayback: FC<MediaPlaybackProps> = (props: MediaPlaybackProps) => {
  const {
    playbackContainerId,
    fullscreenElementId,
    contentId,
    topicId,
    mediaItem = {},
    mediaItemList,
    autoPlay,
    initConfig = {},
    playerOptions = {},
    mediaConfig = {},
    advertisingConfig,
    controlsConfig,
    pluginConfigs,
    trackingConfig,
    handleObservable,
    handleEmbedWrapper,
    handleMediaPlayerReady,
    ...others
  } = props;

  const [internalEmbed, setInternalEmbed] = useState<ImplInternalEmbed | undefined>(undefined);
  const [embedWrapper, setEmbedWrapper] = useState<ImplEmbedWrapper | undefined>(undefined);
  const [observationCenter, setObservationCenter] = useState();

  useEffect(() => {
    // Do we not have a shared instance? Create our own
    if (!internalEmbed) {

      const initConfigDefault = {
        cmsDataRoot: 'https://www.mlb.com'
      };
      const adjustedInitConfig: object = {
        ...initConfigDefault,
        ...initConfig
      };

      const newInternalEmbed: ImplInternalEmbed = InternalEmbed.staticInit();

      newInternalEmbed.init({
        ...adjustedInitConfig
      });

      setInternalEmbed(newInternalEmbed);

    }
  }, []);

  useEffect(() => {
    if (internalEmbed) {
      getEmbedWrapper();
    }
  }, [internalEmbed]);

  useEffect(() => {
    if (observationCenter && handleObservable) {
      handleObservable(observationCenter);
    }
  }, [observationCenter]);

  useDidUpdate(() => {
    if (embedWrapper) {
      embedWrapper.resetPlayback();
      embedWrapper.playClipBySlug(contentId);
    }
  }, [contentId]);

  useDidUpdate(() => {
    if (embedWrapper) {
      embedWrapper.resetPlayback();
      embedWrapper.playClipWithMetadata(mediaItem);
    }
  }, [mediaItem.content_id]);

  const getEmbedWrapper: Function = (): Promise<any> => {

    const {
      autoPlay: playerOptAutoPlay,
      mediaConfig: playerOptMediaConfig = {},
      ...otherPlayerOpts
    }: MediaPlayerOptions = playerOptions;

    let passedInOptions: MediaPlayerOptions = {};

    // Set `fullscreenElementId` if passed in
    if (!!fullscreenElementId) {
      passedInOptions.fullscreenElementId = fullscreenElementId;
    }

    // Set `contentId` if passed in
    if (!!contentId) {
      passedInOptions.contentId = contentId;
    }

    // Set `topicId` if passed in and set `leaveBehindEnabled` to `true`
    if (!!topicId) {
      passedInOptions.leaveBehindEnabled = true;
      passedInOptions.topicId = topicId;
    }

    // Set `list` if `mediaItemList` is passed in
    if (mediaItemList && mediaItemList.length > 0) {
      passedInOptions.leaveBehindEnabled = true;
      passedInOptions.list = mediaItemList;
    }

    if (!!handleMediaPlayerReady) {
      passedInOptions.handleMediaPlayerReady = handleMediaPlayerReady;
    }

    /**
     * autoPlay feature
     * - primary component prop takes precedence
     * - falls through to playbackOptions key if primary prop is undefined
     * - does not get set/passed in otherwise
     **/
    if (autoPlay !== undefined) {
      passedInOptions.autoPlay = autoPlay;
    } else if (playerOptAutoPlay !== undefined) {
      passedInOptions.autoPlay = playerOptAutoPlay;
    }

    if (mediaItem.content_id) {
      passedInOptions.metaData = mediaItem;
    }

    // Build mediaConfig and add to passedInOptions if not empty
    const adjustedMediaConfig = getMediaConfig(
      playerOptMediaConfig,
      mediaConfig,
      advertisingConfig,
      controlsConfig,
      pluginConfigs,
      trackingConfig,
    );
    if (Object.keys(adjustedMediaConfig).length > 0) {
      passedInOptions = { ...passedInOptions, mediaConfig: adjustedMediaConfig };
    }
    return internalEmbed && internalEmbed.createPlayer({
      container: playbackContainerId,
      ...passedInOptions,
      ...otherPlayerOpts
    })
      .then((result: ImplEmbedWrapper) => {
        setEmbedWrapper(result);
        if (handleEmbedWrapper) {
          handleEmbedWrapper(result);
        }
        return result.getObservationCenter();
      })
      .then((result: any) => {
        setObservationCenter(result);
      });
  };

  return <MediaPlaybackDiv
    id={playbackContainerId}
    {...others}
  />;

};

export default MediaPlayback;
